import type { components } from '@/generated/core-api'
import { CoreError } from './errors'

export type OperationProgress = components['schemas']['ImagingExportProgress']

/** A terminal response retains the ordinary transport's refusal vocabulary. */
export async function readOperationStream(
  response: Response,
  onProgress: (progress: OperationProgress) => void,
): Promise<Response> {
  if (!response.headers.get('Content-Type')?.startsWith('application/x-ndjson')) return response
  const reader = response.body?.getReader()
  if (!reader) throw new CoreError('malformed', 'operation stream is missing')
  const decoder = new TextDecoder()
  let buffer = ''
  try {
    while (true) {
      const { done, value } = await reader.read()
      if (done) throw new CoreError('unavailable', 'operation ended without its result')
      buffer += decoder.decode(value, { stream: true })
      // The bound is per record, not elapsed time or total heartbeat traffic.
      if (buffer.length > 32 * 1024 * 1024) throw new CoreError('malformed', 'operation result is too large')
      let end: number
      while ((end = buffer.indexOf('\n')) >= 0) {
        const line = buffer.slice(0, end)
        buffer = buffer.slice(end + 1)
        let event: Record<string, unknown>
        try {
          event = JSON.parse(line)
        } catch {
          throw new CoreError('malformed', 'operation stream is invalid')
        }
        if (!event || typeof event !== 'object') throw new CoreError('malformed', 'operation event is invalid')
        if (event.type === 'waiting') continue
        if (event.type === 'progress') {
          const progress = event.progress as OperationProgress | undefined
          if (!progress || typeof progress.phase !== 'string' ||
            [progress.completedFiles, progress.completedBytes, progress.totalBytes].some(
              (count) => count !== undefined && (!Number.isSafeInteger(count) || count < 0),
            )) throw new CoreError('malformed', 'operation progress is invalid')
          onProgress(progress)
          continue
        }
        if (event.type !== 'result' || !Number.isInteger(event.status) ||
          Number(event.status) < 200 || Number(event.status) > 599 || !('body' in event)) {
          throw new CoreError('malformed', 'operation result is invalid')
        }
        const headers = new Headers(response.headers)
        headers.set('Content-Type', 'application/json')
        return new Response(JSON.stringify(event.body), { status: Number(event.status), headers })
      }
    }
  } finally {
    await reader.cancel().catch(() => undefined)
    reader.releaseLock()
  }
}
